import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useDeviceCommandsStore,
  getCommandStatusLabel,
  COMMAND_STATUS_COLORS,
  isSuccessStatus,
  type SocketCommand,
  type CommandStatus,
} from "../../../../../../../store/admin/deviceCommandsStore";
import api from "../../../../../../../utils/api";
import { useEscapeKey } from "../../../../../../../hooks/useEscapeKey";

interface CommandDetailModalProps {
  command: SocketCommand;
  isDarkMode: boolean;
  onClose: () => void;
}

export default function CommandDetailModal({
  command,
  isDarkMode,
  onClose,
}: CommandDetailModalProps) {
  useEscapeKey(onClose);
  const { t } = useTranslation("lite");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshedData, setRefreshedData] = useState<Partial<SocketCommand> | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  // Store para actualizar el comando globalmente
  const addCommand = useDeviceCommandsStore((state) => state.addCommand);

  // Usar datos refrescados si existen, sino los originales
  const displayCommand = refreshedData ? { ...command, ...refreshedData } : command;

  // Función para refrescar el estado del comando desde el API
  const fetchLatestStatus = async (isInitialLoad = false) => {
    if (!command.request_id) return;

    setIsRefreshing(true);
    if (!isInitialLoad) setRefreshError(null);

    try {
      const response = await api.get(`/commandStatus?request_id=${command.request_id}`);
      const data = response.data;

      if (data.ok && data.command) {
        const newData = {
          status: data.command.status as CommandStatus,
          error: data.command.error,
          raw_request: data.command.raw_request,
          raw_response: data.command.raw_response,
          sent_at: data.command.sent_at,
          finished_at: data.command.finished_at,
        };

        // Verificar si hay cambios respecto al comando actual
        const hasChanges =
          newData.status !== command.status ||
          newData.error !== command.error ||
          JSON.stringify(newData.raw_request) !== JSON.stringify(command.raw_request) ||
          JSON.stringify(newData.raw_response) !== JSON.stringify(command.raw_response);

        if (hasChanges) {
          // Actualizar el store global
          addCommand({
            device_id: command.device_id,
            company_id: command.company_id,
            vehicle_id: command.vehicle_id,
            remolque_id: command.remolque_id,
            command: {
              request_id: command.request_id,
              imei: command.imei ?? undefined,
              brand_id: command.brand_id ?? undefined,
              brand: command.brand ?? undefined,
              model: command.model ?? undefined,
              command_doc_id: command.command_doc_id ?? undefined,
              command_code: command.command_code,
              command_name: command.command_name ?? undefined,
              status: newData.status,
              error: newData.error ?? undefined,
              raw_request: newData.raw_request ?? undefined,
              raw_response: newData.raw_response ?? undefined,
              sent_at: newData.sent_at ?? undefined,
              finished_at: newData.finished_at ?? undefined,
            },
          });
        }

        // Actualizar estado local del modal
        setRefreshedData(newData);
      }
    } catch (err: any) {
      console.error("Error fetching command status:", err);
      if (!isInitialLoad) {
        setRefreshError(err.response?.data?.message || t("commandDetailModal.fetchError"));
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // Cargar los últimos datos al abrir el modal
  useEffect(() => {
    fetchLatestStatus(true);
  }, [command.request_id]);

  // Descomentar si se quiere actualizar estado manualmente
  // const handleRefreshStatus = () => fetchLatestStatus(false);

  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const cardBg = isDarkMode ? "bg-dark-surface" : "bg-gray-50";

  // Formatear timestamp
  const formatDateTime = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  // Obtener color del estado
  const getStatusColor = () => {
    const colors = COMMAND_STATUS_COLORS[displayCommand.status];
    if (!colors) return { bg: "bg-gray-100", text: "text-gray-700" };
    return isDarkMode
      ? { bg: colors.darkBg, text: colors.darkText }
      : { bg: colors.bg, text: colors.text };
  };

  const statusColor = getStatusColor();

  // Renderizar JSON de forma legible
  const renderJson = (obj: Record<string, unknown> | null) => {
    if (!obj) return "-";
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return "-";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`${bgColor} rounded-xl max-w-lg w-full max-h-[85vh] overflow-hidden border ${borderColor} shadow-2xl flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${borderColor}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-terminal-line text-black text-lg"></i>
            </div>
            <div>
              <h2 className={`font-semibold text-lg ${textColor}`}>
                {t("commandDetailModal.title")}
              </h2>
              <p className={`text-xs ${textSecondary}`}>
                {command.command_name || command.command_code}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            } rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${textColor}`}></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Estado del comando */}
          <div className={`p-4 rounded-lg ${cardBg}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <i className={`ri-pulse-line ${textSecondary}`}></i>
                <p className={`text-xs font-medium ${textColor}`}>
                  {t("commandDetailModal.commandStatus")}
                </p>
              </div>
              {/* Descomentar si se quiere actualizar estado manualmente
              <button
                onClick={handleRefreshStatus}
                disabled={isRefreshing}
                className={`text-xs px-2 py-1 rounded border ${borderColor} ${textSecondary} hover:text-primary-500 hover:border-primary-500 transition-colors disabled:opacity-50`}
              >
                {isRefreshing ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <><i className="ri-refresh-line mr-1"></i>{t("commandDetailModal.refresh")}</>
                )}
              </button>
              */}
            </div>

            {/* Estado */}
            <div className="mb-3">
              <p className={`text-[10px] ${textSecondary} mb-1`}>
                {t("commandDetailModal.status")}
              </p>
              <span
                className={`inline-block px-3 py-1.5 rounded-lg text-sm font-medium ${statusColor.bg} ${statusColor.text}`}
              >
                {getCommandStatusLabel(displayCommand.status)}
              </span>
              {/* Descripción del estado */}
              <p className={`text-xs ${textSecondary} mt-2`}>
                {displayCommand.status === "pending_route" && t("commandDetailModal.statusPendingRoute")}
                {displayCommand.status === "queued_for_route" && t("commandDetailModal.statusQueuedForRoute")}
                {displayCommand.status === "queued" && t("commandDetailModal.statusQueued")}
                {displayCommand.status === "sending" && t("commandDetailModal.statusSending")}
                {displayCommand.status === "sent" && t("commandDetailModal.statusSent")}
                {displayCommand.status === "ack" && t("commandDetailModal.statusAck")}
                {displayCommand.status === "timeout" && t("commandDetailModal.statusTimeout")}
                {displayCommand.status === "offline" && t("commandDetailModal.statusOffline")}
                {displayCommand.status === "unsupported" && t("commandDetailModal.statusUnsupported")}
                {displayCommand.status === "error" && t("commandDetailModal.statusError")}
                {displayCommand.status === "rejected" && t("commandDetailModal.statusRejected")}
                {displayCommand.status === "expired" && t("commandDetailModal.statusExpired")}
                {displayCommand.status === "write_failed" && t("commandDetailModal.statusWriteFailed")}
              </p>
            </div>

            {/* Error: el status manda. Solo mostramos el texto del `error` cuando el estado
                es de fallo; en estados de éxito ese campo puede traer un motivo no fatal
                (p. ej. "queued_for_route" con status="pending_route") y no es un error. */}
            {!isSuccessStatus(displayCommand.status) && displayCommand.error && (
              <div className="mt-3">
                <p className={`text-[10px] ${textSecondary} mb-1`}>
                  {t("commandDetailModal.error")}
                </p>
                <div className={`${isDarkMode ? "bg-red-900/30 text-red-400" : "bg-red-100 text-red-700"} px-3 py-2 rounded-lg text-sm`}>
                  {displayCommand.error}
                </div>
              </div>
            )}

            {refreshError && (
              <p className="text-red-500 text-xs mt-2">{refreshError}</p>
            )}
          </div>

          {/* Información general */}
          <div className={`p-4 rounded-lg ${cardBg}`}>
            <p className={`text-xs ${textSecondary} mb-3`}>
              {t("commandDetailModal.generalInfo")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className={`text-[10px] ${textSecondary}`}>
                  {t("commandDetailModal.commandCode")}
                </p>
                <p className={`text-sm ${textColor} font-mono`}>{command.command_code}</p>
              </div>
              <div>
                <p className={`text-[10px] ${textSecondary}`}>
                  {t("commandDetailModal.commandName")}
                </p>
                <p className={`text-sm ${textColor}`}>{command.command_name || "-"}</p>
              </div>
              <div>
                <p className={`text-[10px] ${textSecondary}`}>
                  {t("commandDetailModal.brand")}
                </p>
                <p className={`text-sm ${textColor}`}>{command.brand || "-"}</p>
              </div>
              <div>
                <p className={`text-[10px] ${textSecondary}`}>
                  {t("commandDetailModal.model")}
                </p>
                <p className={`text-sm ${textColor}`}>{command.model || "-"}</p>
              </div>
            </div>
          </div>

          {/* Identificadores */}
          <div className={`p-4 rounded-lg ${cardBg}`}>
            <p className={`text-xs ${textSecondary} mb-3`}>
              {t("commandDetailModal.identifiers")}
            </p>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <p className={`text-[10px] ${textSecondary}`}>Request ID</p>
                <p className={`text-xs ${textColor} font-mono break-all`}>{command.request_id}</p>
              </div>
              {command.imei && (
                <div>
                  <p className={`text-[10px] ${textSecondary}`}>IMEI</p>
                  <p className={`text-xs ${textColor} font-mono`}>{command.imei}</p>
                </div>
              )}
              {command.device_id && (
                <div>
                  <p className={`text-[10px] ${textSecondary}`}>Device ID</p>
                  <p className={`text-xs ${textColor} font-mono break-all`}>{command.device_id}</p>
                </div>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div className={`p-4 rounded-lg ${cardBg}`}>
            <p className={`text-xs ${textSecondary} mb-3`}>
              {t("commandDetailModal.timestamps")}
            </p>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <p className={`text-[10px] ${textSecondary}`}>
                  {t("commandDetailModal.receivedAt")}
                </p>
                <p className={`text-sm ${textColor}`}>{formatDateTime(displayCommand.receivedAt)}</p>
              </div>
              {displayCommand.sent_at && (
                <div>
                  <p className={`text-[10px] ${textSecondary}`}>
                    {t("commandDetailModal.sentAt")}
                  </p>
                  <p className={`text-sm ${textColor}`}>{formatDateTime(displayCommand.sent_at)}</p>
                </div>
              )}
              {displayCommand.finished_at && (
                <div>
                  <p className={`text-[10px] ${textSecondary}`}>
                    {t("commandDetailModal.finishedAt")}
                  </p>
                  <p className={`text-sm ${textColor}`}>{formatDateTime(displayCommand.finished_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Raw Request - Se envió al dispositivo */}
          {displayCommand.raw_request && (
            <div className={`p-4 rounded-lg ${cardBg}`}>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-upload-2-line text-blue-400"></i>
                <p className={`text-xs ${textSecondary}`}>
                  {t("commandDetailModal.rawRequest")}
                </p>
              </div>
              <div className="bg-[#1e1e1e] rounded-lg p-3 border border-gray-600 overflow-x-auto">
                <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                  {renderJson(displayCommand.raw_request)}
                </pre>
              </div>
            </div>
          )}

          {/* Raw Response - El dispositivo respondió */}
          {displayCommand.raw_response && (
            <div className={`p-4 rounded-lg ${cardBg}`}>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-download-2-line text-green-400"></i>
                <p className={`text-xs ${textSecondary}`}>
                  {t("commandDetailModal.rawResponse")}
                </p>
              </div>
              <div className="bg-[#1e1e1e] rounded-lg p-3 border border-gray-600 overflow-x-auto">
                <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                  {renderJson(displayCommand.raw_response)}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`p-4 border-t ${borderColor} flex justify-end`}
        >
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors"
          >
            {t("commandDetailModal.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
